import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, ButtonGroup, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { fetchError, getOrgProgress, getProgressOfUsers, getUsers } from "../../redux/actions";
import { theme } from "../../themes/theme";
import { VirtualTeam } from "../../types/models/Team";
import { AppState } from "../../redux/store";
import { TeamMembersState } from "../../types/models/TeamMembersState";
import AppInfoView from "../../components/AppBase/AppInfoView";
import AppLoader from "../../components/AppBase/AppLoader";
import YourTeamHeader from "./YourTeamHeader";
import MemberTable from "./MemberTable";
import EditTeam from "../all-teams/EditTeam";
import EmptyResult from "../rules/EmptyResult";
import { TeamMemberProgress } from "../../types/models/TeamMember";
import { useTeams } from "../../hooks/useTeams";
import { Acknowledgment } from "../../types/models/Acknowledgement";
import { CSVData } from "../../types/models/TeamCSVData";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { grey } from "@mui/material/colors";

export const MyTeamId = "my-team";

export default function YourTeamsPage() {
  const dispatch = useDispatch();
  const useTeamsHook = useTeams();
  const teamState = useSelector<AppState, TeamMembersState>((state) => state.teamMembers);

  const [loading, setLoading] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<VirtualTeam | undefined>({
    TeamId: MyTeamId,
    DisplayName: "",
    StartDate: "",
    EndDate: "",
    Members: [],
  });
  const [targetTeamProgress, setTargetTeamProgress] = useState<TeamMemberProgress[] | undefined>();
  const [displayTeamProgress, setDisplayTeamProgress] = useState<
    TeamMemberProgress[] | undefined
  >();
  const [displayAllTeamMember, setDisplayAllTeamMember] = useState<boolean>(false);
  const [isPreparingDownload, setIsPreparingDownload] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentLimit, setCurrentLimit] = useState<number>(20);
  const [hasNext, setHasNext] = useState<boolean | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const csvLinkRef = useRef<unknown>(null);

  const convertToCSV = (data: CSVData[], headers: Array<{ label: string; key: string }>) => {
    const csvHeaders = headers.map((header) => header.label).join(",");
    const csvRows = data.map((row) =>
      headers
        .map((header) => {
          const value = row[header.key];
          if (typeof value === "string" && (value.includes(",") || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",")
    );
    return [csvHeaders, ...csvRows].join("\n");
  };

  // Manual CSV download
  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const getDataForDownload = () => {
    if (isPreparingDownload) return;

    setIsPreparingDownload(true);

    // Function to process team progress data and trigger download
    const processTeamProgressAndDownload = (teamData: CSVData[]) => {
      // Create headers for CSV
      const headers = [
        { label: "Email", key: "email" },
        { label: "Name", key: "name" },
        { label: "Registration Date", key: "registrationDate" },
        { label: "Last Acknowledged", key: "lastAcknowledged" },
        { label: "Total Alerts Overdue", key: "totalAlertsOverdue" },
        { label: "Total Unresponded Message Count", key: "totalUnrespondedMessageCount" },
        { label: "Oldest Unresponded Message Date", key: "oldestUnrespondedMessageDate" },
        { label: "Last Sync Start Date", key: "lastSyncStartDate" },
        { label: "Assigned RoleBundles", key: "assignedRoleBundles" },
        { label: "Assigned Roles", key: "assignedRoles" },
        { label: "Opt In Roles", key: "optInRoles" },
        { label: "Locations", key: "locations" },
        { label: "Total Alerts Ackowledged", key: "totalAlertsAcknowledged" },
        { label: "Days Since Acknowledged", key: "daysSinceAcknowledged" },
        { label: "Days Since Sync", key: "daysSinceSync" },
      ];

      // Convert to CSV and download
      const csvContent = convertToCSV(teamData, headers);
      const filename = selectedTeam?.DisplayName
        ? `${selectedTeam.DisplayName}-data.csv`
        : "Org-data.csv";

      downloadCSV(csvContent, filename);
      setIsPreparingDownload(false);
    };

    const formatDate = (dateString: string | null | undefined): string => {
      if (!dateString) return "";

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";

        return date.toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      } catch (error) {
        return "";
      }
    };

    const getCSVDataFromTeamAndUserProgress = (
      teamMember: TeamMemberProgress,
      allProgress: Acknowledgment[]
    ) => {
      //Get acknowledgements for user
      const memberProgress = allProgress.filter((ack) => ack.UserId === teamMember.user_id);

      const lastAcknowledgedDate = memberProgress.reduce((latest, ack) => {
        if (ack.AcknowledgeAt && (!latest || new Date(ack.AcknowledgeAt) > new Date(latest))) {
          return formatDate(ack.AcknowledgeAt);
        }
        return formatDate(latest);
      }, null as string | null);

      const oldestUnrespondedDate = memberProgress
        .filter((ack) => !ack.AcknowledgeAt && ack.RecievedAt)
        .reduce((oldest, ack) => {
          if (ack.RecievedAt && (!oldest || new Date(ack.RecievedAt) < new Date(oldest))) {
            return formatDate(ack.RecievedAt);
          }
          return formatDate(oldest);
        }, null as string | null);

      const totalUnresponded = memberProgress.filter((ack) => ack.SenderId !== ack.UserId).length;

      const daysSinceAcknowledged = lastAcknowledgedDate
        ? formatDate(
            Math.floor(
              (Date.now() - new Date(lastAcknowledgedDate).getTime()) / (1000 * 60 * 60 * 24)
            ).toString()
          )
        : "Unknown";

      const lastSyncDate = memberProgress.reduce((latest, ack) => {
        if (
          ack.LastSyncStartDate &&
          (!latest || new Date(ack.LastSyncStartDate) > new Date(latest))
        ) {
          return formatDate(ack.LastSyncStartDate);
        }
        return formatDate(latest);
      }, null as string | null);

      const daysSinceSync = lastSyncDate
        ? formatDate(
            Math.floor(
              (Date.now() - new Date(lastSyncDate).getTime()) / (1000 * 60 * 60 * 24)
            ).toString()
          )
        : "Unknown";

      return {
        email: teamMember.email,
        name: teamMember.name,
        registrationDate: "Unknown",
        lastAcknowledged: lastAcknowledgedDate || "Unknown",
        totalAlertsOverdue: teamMember.overdueCount,
        totalUnrespondedMessageCount: totalUnresponded,
        oldestUnrespondedMessageDate: oldestUnrespondedDate || "Unknown",
        lastSyncStartDate: lastSyncDate || "Unknown",
        assignedRoleBundles: teamMember.roleBundles?.map((bundle) => bundle.name).join(", ") ?? "",
        assignedRoles: Array.isArray(teamMember.roles)
          ? teamMember.roles.join(", ")
          : teamMember.roles || "",
        optInRoles: "Unknown",
        locations: Array.isArray(teamMember.locations)
          ? teamMember.locations.join(", ")
          : teamMember.locations || "Unknown",
        totalAlertsAcknowledged: teamMember.acknowledgedCount,
        daysSinceAcknowledged: daysSinceAcknowledged || "Unknown",
        daysSinceSync: daysSinceSync || "Unknown",
      };
    };

    // Function to get users progress
    const getUsersProgress = (userIds: string[]) => {
      dispatch(
        getProgressOfUsers(userIds, (success) => {
          if (success) {
            const orgProgress = teamState.orgProgress ?? [];
            const progressOfUsers = teamState.progressOfUsers ?? [];

            const csvData: CSVData[] = [];

            const convertedOrgProgress = orgProgress.map((teamMember) =>
              getCSVDataFromTeamAndUserProgress(teamMember, progressOfUsers)
            );
            csvData.push(...convertedOrgProgress);

            if (csvData.length > 0) {
              //process data for download
              processTeamProgressAndDownload(csvData);
            } else {
              dispatch(fetchError("No team data to download"));
              console.warn("No team data to download");
              setIsPreparingDownload(false);
            }
          } else {
            dispatch(fetchError("Failed to get team data"));
            console.error("Failed to get team data");
            setIsPreparingDownload(false);
          }
        })
      );
    };

    // Determine which users to get progress for and call the API
    if (!selectedTeam || selectedTeam?.TeamId === MyTeamId) {
      if (!teamState.orgProgress) {
        console.error("No org progress data available");
        setIsPreparingDownload(false);
        return;
      }

      const orgUserIds = teamState.orgProgress
        ?.map((progress) => progress.user_id ?? "")
        .filter((id) => id !== "");

      if (orgUserIds.length === 0) {
        console.error("No valid user IDs found in org progress");
        setIsPreparingDownload(false);
        return;
      }

      getUsersProgress(orgUserIds);
    } else {
      const currentTeamUserIds = selectedTeam?.Members.filter((member) => member.UserId !== "").map(
        (member) => member.UserId
      );

      if (!currentTeamUserIds || currentTeamUserIds.length === 0) {
        console.error("No team member user IDs available");
        setIsPreparingDownload(false);
        return;
      }

      getUsersProgress(currentTeamUserIds);
    }
  };

  const getOrgProgressData = useCallback(() => {
    dispatch(
      getOrgProgress(currentPage, currentLimit, (_, hasNextData, totalCountData) => {
        setHasNext(hasNextData);
        setTotalCount(totalCountData);
        setLoading(false);
      })
    );
  }, [dispatch, currentPage, currentLimit]);

  useEffect(() => {
    setLoading(true);
    if (selectedTeam?.TeamId === MyTeamId) {
      getOrgProgressData();
    } else if (!teamState.users) {
      dispatch(
        getUsers(() => {
          setLoading(false);
        })
      );
    }
  }, []);

  // Set currentPage to 0 when the limit changes
  useEffect(() => {
    setCurrentPage(0);
  }, [currentLimit]);

  useEffect(() => {
    setLoading(true);
    getOrgProgressData();
  }, [currentPage, getOrgProgressData]);

  // const getHeaderActionElement = () => {
  //     if (isCurrentAccountOrgManager() && (selectedTeam && (selectedTeam.TeamId !== MyTeamId))) {
  //         return (
  //                 <Button
  //                     startIcon={<EditIcon />}
  //                     variant="text"
  //                     onClick={() => {
  //                         setOpenEditModal(true);
  //                     }}
  //                     sx={{ width: "130px", textTransform: "none", borderRadius: 1 }}>
  //                         Edit Team
  //                 </Button>
  //         );
  //     } else {
  //         return undefined;
  //     }
  // };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <AppLoader />
        </Box>
      )}
      <Box sx={{ backgroundColor: theme.page.bgColor, minHeight: "100vh" }}>
        <YourTeamHeader
          getDataForDownload={getDataForDownload}
          teamName={selectedTeam?.DisplayName}
          loading={loading}
          title="Team Members"
          // action={getHeaderActionElement()}
          onSelectTeam={(teamId) => {
            if (teamId === MyTeamId) {
              // Select "My Team"
              setDisplayAllTeamMember(true);
              setSelectedTeam(undefined);
            } else {
              setDisplayAllTeamMember(false);
              setSelectedTeam(teamState.myTeams?.find((team) => team.TeamId === teamId));
            }
          }}
          orgProgress={teamState.orgProgress}
          isPreparingDownload={isPreparingDownload}
          csvLinkRef={csvLinkRef}
        />
        <Box sx={{ m: 2 }}>
          {!loading &&
            teamState.myTeams &&
            (selectedTeam || displayAllTeamMember ? (
              <MemberTable
                allMemberProgress={displayAllTeamMember}
                team={selectedTeam}
                onOpenEditDialog={() => setOpenEditModal(true)}
                displayTeamProgress={displayTeamProgress}
                setDisplayTeamProgress={setDisplayTeamProgress}
                targetTeamProgress={targetTeamProgress}
                setTargetTeamProgress={setTargetTeamProgress}
                useTeamsInstance={useTeamsHook}
              />
            ) : (
              <EmptyResult>No team selected</EmptyResult>
            ))}
        </Box>
        {(displayAllTeamMember || selectedTeam?.TeamId === MyTeamId) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px",
              visibility: loading ? "hidden" : "visible",
              margin: "0px 10px",
            }}
          >
            <Box>
              <Typography sx={{ fontSize: "14px", color: grey }}>
                {currentLimit * currentPage + 1} -{" "}
                {Math.min(currentLimit * (currentPage + 1), totalCount ?? 0)} of {totalCount} row(s)
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography sx={{ marginRight: "10px" }}>Rows per page</Typography>
              <FormControl sx={{ marginRight: "10px" }} size="small">
                <Select
                  value={currentLimit}
                  onChange={(e) => setCurrentLimit(e.target.value as number)}
                >
                  {Array.from([10, 20, 30, 40, 50]).map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <ButtonGroup variant="outlined">
                <Button size="small" disabled={currentPage === 0} onClick={() => setCurrentPage(0)}>
                  <KeyboardDoubleArrowLeftIcon />
                </Button>
                <Button
                  size="small"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <KeyboardArrowLeftIcon />
                </Button>
                <Button
                  size="small"
                  disabled={!hasNext}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <KeyboardArrowRightIcon />
                </Button>
                <Button
                  size="small"
                  disabled={!hasNext}
                  onClick={() => setCurrentPage(Math.floor((totalCount ?? 0) / currentLimit))}
                >
                  <KeyboardDoubleArrowRightIcon />
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        )}
      </Box>
      <EditTeam
        team={selectedTeam}
        isOpen={openEditModal}
        onClose={() => setOpenEditModal(false)}
        allUsers={teamState.users ?? []}
        setTargetTeamProgress={setTargetTeamProgress}
        isYourTeamsPage={true}
      ></EditTeam>
      <AppInfoView />
    </>
  );
}
